import { startTransition } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import type { RootState } from '@redux/RootReducer';

import { useAuth } from '@nvision/identity-core';
import { Checkbox, Tag, Text } from '@nvision/ui';

import { Card } from '@/components/card';
import { useCompanyUsers } from '@/modules/entities';

import { ROUTES } from '../../../config';
import type { Contract } from '../../../models';

import styles from './contract-card.module.css';

interface ContractCardProps {
  contract: Contract;
}

const DATE_FORMAT = 'dd-MMM-yyyy';
const EMPTY_VALUE = '—';

const STATUS_TAG_PROPS: Record<string, React.ComponentProps<typeof Tag>> = {
  Active: { success: true },
  Draft: { secondary: true },
  Expired: { warn: true },
};

const formatDateValue = (value: string | null | undefined, timeZone?: string) => {
  if (!value) {
    return EMPTY_VALUE;
  }

  return timeZone ? formatInTimeZone(value, timeZone, DATE_FORMAT) : format(value, DATE_FORMAT);
};

export const ContractCard: React.FC<ContractCardProps> = ({ contract }) => {
  const navigate = useNavigate();
  const auth = useAuth();
  const timeZone = auth.user?.profile.pref.timezone;
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany, shallowEqual);

  const { data: companyUsers } = useCompanyUsers({
    companyId: String(selectedCompany?.companyId ?? ''),
    searchParams: {
      ids: [contract.createdBy, contract.modifiedBy].filter((id) => id !== null && id !== undefined).map(String),
    },
  });

  const tagProps = STATUS_TAG_PROPS[contract.status ?? ''] ?? { secondary: true };

  const handleClick = () => {
    startTransition(() => {
      navigate(ROUTES.contract(contract.key));
    });
  };

  return (
    <Card onClick={handleClick} style={{ cursor: 'pointer' }}>
      <Card.Header>
        <div className={styles.header}>
          <Checkbox className={styles.checkbox} size="sm" />
          <Text size="sm" weight="semibold">
            {contract.name || EMPTY_VALUE}
          </Text>
          <Tag {...tagProps} size="sm">
            <Text size="sm">{contract.status ?? EMPTY_VALUE}</Text>
          </Tag>
        </div>
      </Card.Header>
      <Card.Main>
        <div className={styles.grid}>
          <Card.Field>
            <Text size="xs" elementType="div">
              Contract Number
            </Text>
            <Text size="xs" elementType="div" weight="semibold">
              {contract.uniqueName || EMPTY_VALUE}
            </Text>
          </Card.Field>
          <Card.Field>
            <Text size="xs" elementType="div">
              Effective From
            </Text>
            <Text size="xs" elementType="div" weight="semibold">
              {formatDateValue(contract.effectiveFrom, timeZone)}
            </Text>
          </Card.Field>
          <Card.Field>
            <Text size="xs" elementType="div">
              Pricing Basis
            </Text>
            <Text size="xs" elementType="div" weight="semibold">
              {contract.pricingBasis?.label || EMPTY_VALUE}
            </Text>
          </Card.Field>
          <Card.Field>
            <Text size="xs" elementType="div">
              Supplier Diversity
            </Text>
            <Text size="xs" elementType="div" weight="semibold">
              {contract.supplierDiversity?.label || EMPTY_VALUE}
            </Text>
          </Card.Field>
          <Card.Field>
            <Text size="xs" elementType="div">
              Customer Lane ID
            </Text>
            <Text size="xs" elementType="div" weight="semibold">
              {contract.customerLaneId?.label ?? EMPTY_VALUE}
            </Text>
          </Card.Field>

          <Card.Field>
            <Text size="xs" elementType="div">
              SCAC
            </Text>
            <Text size="xs" elementType="div" weight="semibold">
              {contract.carrier?.carrierScacCode || EMPTY_VALUE}
            </Text>
          </Card.Field>
          <Card.Field>
            <Text size="xs" elementType="div">
              Effective To
            </Text>
            <Text size="xs" elementType="div" weight="semibold">
              {formatDateValue(contract.effectiveTo, timeZone)}
            </Text>
          </Card.Field>
          <Card.Field>
            <Text size="xs" elementType="div">
              Pricing Type
            </Text>
            <Text size="xs" elementType="div" weight="semibold">
              {contract.pricingType?.label || EMPTY_VALUE}
            </Text>
          </Card.Field>
          <Card.Field>
            <Text size="xs" elementType="div">
              Created By
            </Text>
            <Text size="xs" elementType="div" weight="semibold">
              {companyUsers.map[String(contract.createdBy)]?.fullName ?? contract.createdBy ?? EMPTY_VALUE}
            </Text>
          </Card.Field>
          <Card.Field>
            <Text size="xs" elementType="div">
              File Name
            </Text>
            <Text size="xs" elementType="div" weight="semibold">
              {EMPTY_VALUE}
            </Text>
          </Card.Field>

          <Card.Field>
            <Text size="xs" elementType="div">
              Transportation Provider
            </Text>
            <Text size="xs" elementType="div" weight="semibold">
              {contract.carrier?.carrierName || EMPTY_VALUE}
            </Text>
          </Card.Field>
          <Card.Field>
            <Text size="xs" elementType="div">
              Cargo Type
            </Text>
            <Text size="xs" elementType="div" weight="semibold">
              {contract.cargoType?.label || EMPTY_VALUE}
            </Text>
          </Card.Field>
          <Card.Field>
            <Text size="xs" elementType="div">
              Pricing Category
            </Text>
            <Text size="xs" elementType="div" weight="semibold">
              {contract.pricingCategory?.label || EMPTY_VALUE}
            </Text>
          </Card.Field>
          <Card.Field>
            <Text size="xs" elementType="div">
              Modified By
            </Text>
            <Text size="xs" elementType="div" weight="semibold">
              {companyUsers.map[String(contract.modifiedBy)]?.fullName ?? contract.modifiedBy ?? EMPTY_VALUE}
            </Text>
          </Card.Field>
          <Card.Field>
            <Text size="xs" elementType="div">
              Comments
            </Text>
            <Text size="xs" elementType="div" weight="semibold">
              {EMPTY_VALUE}
            </Text>
          </Card.Field>
        </div>
      </Card.Main>
    </Card>
  );
};
